import React, { useState } from 'react';
import { Button, ButtonProps } from '@mui/material';
import { Star, Upgrade } from '@mui/icons-material';
import PaymentCheckout from './PaymentCheckout';
import { useAuth } from '../context/AuthContext';
import { PRICING } from '../utils/pricing';

interface PremiumUpgradeButtonProps extends Omit<ButtonProps, 'onClick'> {
  providerId: number;
  currentTier?: 'basic' | 'premium' | 'platinum';
  variant?: 'contained' | 'outlined' | 'text';
}

const PremiumUpgradeButton: React.FC<PremiumUpgradeButtonProps> = ({
  providerId,
  currentTier = 'basic',
  variant = 'contained',
  ...buttonProps
}) => {
  const { user } = useAuth();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'premium' | 'platinum'>('premium');

  const tierPricing = {
    premium: PRICING.PREMIUM_MONTHLY,
    platinum: PRICING.PLATINUM_MONTHLY
  };

  const handleUpgrade = (tier: 'premium' | 'platinum') => {
    setSelectedTier(tier);
    setCheckoutOpen(true);
  };

  if (!user) {
    return null;
  }

  if (currentTier === 'platinum') {
    return (
      <Button
        variant={variant}
        disabled
        startIcon={<Star />}
        {...buttonProps}
      >
        Already Platinum
      </Button>
    );
  }

  return (
    <>
      {currentTier === 'basic' && (
        <>
          <Button
            variant={variant}
            color="primary"
            startIcon={<Star />}
            onClick={() => handleUpgrade('premium')}
            sx={{ mr: 1 }}
            {...buttonProps}
          >
            Upgrade to Premium (R{tierPricing.premium}/mo)
          </Button>
          <Button
            variant={variant}
            color="secondary"
            startIcon={<Star />}
            onClick={() => handleUpgrade('platinum')}
            {...buttonProps}
          >
            Upgrade to Platinum (R{tierPricing.platinum}/mo)
          </Button>
        </>
      )}

      {currentTier === 'premium' && (
        <Button
          variant={variant}
          color="secondary"
          startIcon={<Upgrade />}
          onClick={() => handleUpgrade('platinum')}
          {...buttonProps}
        >
          Upgrade to Platinum (R{tierPricing.platinum}/mo)
        </Button>
      )}

      <PaymentCheckout
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        paymentType="subscription"
        amount={tierPricing[selectedTier]}
        title={`Upgrade to ${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Listing`}
        description={`Upgrade your listing to ${selectedTier} tier for enhanced visibility and features.`}
        providerId={providerId}
        metadata={{
          subscriptionType: selectedTier === 'premium' ? 'premium_listing' : 'platinum_listing',
          plan: 'monthly'
        }}
        onSuccess={() => {
          setCheckoutOpen(false);
          window.location.reload(); // Refresh to show updated tier
        }}
      />
    </>
  );
};

export default PremiumUpgradeButton;

